import * as moment from 'moment';

import { Attributes, DependencyDto, IValueViewModel, ObjectType, SeriesDefinition, TagDateValue } from '../../models';
import { PaneDefinition } from './PaneDefinition';
import { IDateRangeService, IidentifierService, IValuesService, IwidgetIntervalHelperService } from '../../services';
import { getPopoverCallback, goMethod } from '../../widgets/common/callback';
import { MissingFormat } from './MissingFormat';
import { ReadResult } from './ReadResult';
import { infoTextColor } from '../../common/constants';
import { popoverItem } from '../../common/helpers';

/**
 * Maps an identfier type (see IdentifierType) to a unicode fa icon
 * Mapped from identifierIcon.component.html
 */
const iconsDic = {
    0: '\uf02b', //Measured
    1: '\uf1ec', //Calculated
    2: '\uf0c1', // Linked
    3: '\uf046', // Validated
    4: '\uf192' // Target
};

export interface IidentifierAnalysisService {
    readSeries(guid: string, viewModels: { [guid: string]: IValueViewModel }, refGroupGuid: string): angular.IPromise<{ observedIdentifier: DependencyDto[], dependencyDto: DependencyDto[], measuredDto: DependencyDto[] }>;
    readData(dependencyList: DependencyDto[], force?: boolean): angular.IPromise<ReadResult>;
    // refresh(beginDate: string, endDate: string, dependencyList: DependencyDto[], force?: boolean): angular.IPromise<ReadResult>;
    mapSeries(dependencyList: DependencyDto[], viewModels: { [guid: string]: IValueViewModel }): { panes: PaneDefinition[], series: SeriesDefinition<any>[] };
    mapData(data: { values: TagDateValue[]; missing: MissingFormat[]; }, dependencyStatuses: any[]): ReadResult;
}

export class IdentifierAnalysisService implements IidentifierAnalysisService {
    static serviceId = 'identifierAnalysisService';

    static $inject = ['$q',
        '$state',
        'identifierService',
        'dateRangeService',
        'valuesService',
        'widgetIntervalHelperService'];
    constructor(private _$q: angular.IQService,
        private _$state: angular.ui.IStateService,
        private _identifierService: IidentifierService,
        private _dateRangeService: IDateRangeService,
        private _valuesService: IValuesService,
        private _widgetIntervalHelperService: IwidgetIntervalHelperService) {
    }

    readSeries(guid: string, viewModels: { [guid: string]: IValueViewModel }, refGroupGuid: string) {
        return this._identifierService.getDependency(guid)
            .then(data => {
                data = data.map(d => {
                    const vm = viewModels[d.guid];
                    const groupGuid = vm.groupList[0].guid;
                    const newName = groupGuid === refGroupGuid ? vm.name : d.name;
                    return (Object as any).assign({}, d, { name: newName });
                });
                const observedIdentifier = data.filter(d => d.guid === guid);
                const dependencyDto = data.filter(d => d.guid !== guid && !d.isMeasured);
                const measuredDto = data.filter(d => d.guid !== guid && d.isMeasured);
                return { observedIdentifier, dependencyDto, measuredDto };
            });
    }

    mapSeries(dependencyList: DependencyDto[], viewModels: { [guid: string]: IValueViewModel }): { panes: PaneDefinition[], series: SeriesDefinition<any>[] } {
        const panes = this.getPanes(dependencyList, viewModels, this._$state.go);
        const series: SeriesDefinition<any>[] = dependencyList.map((dout, i) => {
            return {
                name: dout.name,
                identifier: dout.guid,
                color: infoTextColor,
                selector: d => d.guid,
                value: d => d.value,
                isLineVisible: true,
                pane: i,
                seriesType: 'area',
                y0: d => (d.value === null || d.value === undefined || d.value < 0) ? d.value : 0,
                y1: d => (d.value !== null && d.value !== undefined && d.value < 0) ? 0 : d.value
            };
        });
        return { panes, series };
    }

    private getPanes(data: DependencyDto[], viewModels: { [guid: string]: IValueViewModel }, go: goMethod) {

        const panes: any = data.map(d => {
            const vm = viewModels[d.guid];
            const popover = vm.objectType === ObjectType.Tag
                ? 'tagPopover.html'
                : 'identifierPopover.html';
            const attributes: Attributes = {
                'wt-popover-svg': 'true',
                item: popoverItem(vm),
                'template': popover,
                'compile': 'wt-popover-svg',
                placement: 'bottom'
            };
            return {
                isXaxisVisible: false,
                isYaxisVisible: true,
                height: 60,
                isTitleVisible: true,
                title: d.name,
                titleClickCallback: getPopoverCallback(vm, go),
                iconClickCallback: () => this._$state.go('group.analysis', { groupGuid: vm.groupList[0].guid, identifierGuid: vm.guid }),
                titleAttributes: attributes,
                titleFaIcon: iconsDic[vm.identifierType],
                editMode: false,
                ticksCount: 1,
                isTitleLeftAligned: true
            };
        });
        if (panes.length) {
            const last = panes.reverse()[0];
            last.height = 80;
            last.isXaxisVisible = true;
            panes.reverse();
        }
        return panes;
    }

    readData(dependencyList: DependencyDto[], force: boolean = false): angular.IPromise<ReadResult> {
        const defaultInterval = this._widgetIntervalHelperService.getCurrentInclusiveInterval();
        const beginDate = defaultInterval.beginDate;
        const endDate = defaultInterval.endDate;
        return this.refresh(beginDate, endDate, dependencyList, force);
    }

    private refresh(beginDate: string, endDate: string, dependencyList: DependencyDto[], force: boolean = false) {
        const series = dependencyList.map(d => {
            return {
                guid: d.guid,
                value: 'average'
            };
        });
        return this._$q.all([
            this._valuesService.getValuesWithMissing(series, beginDate, endDate, force),
            this._identifierService.getDependencyStatuses(dependencyList, beginDate, endDate, force)
        ])
            .then(responses => {
                const data = responses[0];
                const dependencyStatuses = responses[1];
                return this.mapData(data, dependencyStatuses);
            });
    }

    mapData(data: { values: TagDateValue[]; missing: MissingFormat[]; }, dependencyStatuses: any[]) {
        const errors: { [guid: string]: PaneDefinition[]; } = {};
        const filteredData = data.values
            .filter(d => d.value !== null && d.value !== undefined);
        data.missing.forEach(m => {
            const list: PaneDefinition[] = m.value.map(v => {
                return {
                    name: 'Missing',
                    color: 'rgba(255,0,0,0.5)',
                    beginDate: moment(v.start).toDate(),
                    endDate: moment(v.end).toDate()
                };
            });
            if (dependencyStatuses && dependencyStatuses[m.key]) {
                const mapped: PaneDefinition[] = dependencyStatuses[m.key].map(e => {
                    return {
                        name: e.name,
                        color: e.color === 'orange' ? 'rgba(255,153,51,0.5)' : 'rgba(255,0,0,0.5)',
                        beginDate: moment(e.beginDateTime).toDate(),
                        endDate: moment(e.endDateTime).toDate()
                    };
                });
                mapped.forEach(d => {
                    if (!list.filter(f => f.beginDate.toISOString() === d.beginDate.toISOString()
                        && f.endDate.toISOString() === d.endDate.toISOString()).length) {
                        list.push(d);
                    }
                });
            }
            errors[m.key] = list;
        });
        return {
            xMin: moment(this._dateRangeService.beginDate() as string).toDate(),
            xMax: moment(this._dateRangeService.endDate() as string).toDate(),
            data: filteredData,
            errors
        };
    }
}